import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { Router, RouterLink } from "@angular/router";
import { AuthenticationService } from "app/auth/authentication.service";
import { User } from "app/auth/user";
import { LoadingUntilFirstEmitDirective } from "app/common-ux/loading/loading-until-first-emit.directive";
import { ResponsiveService } from "app/common-ux/responsive.service";
import { UserDropdownComponent } from "app/common-ux/user-dropdown/user-dropdown.component";
import { map, Observable } from "rxjs";
import { TenantDto } from "app/dal/generated/models/security/tenant-dto";
import { TenantService } from "../tenant.service";

interface TenantWrapper {
    tenant: TenantDto;
    image?: string;
}

@Component({
    selector: "app-select-tenant-page",
    templateUrl: "./select-tenant-page.component.html",
    styleUrl: "./select-tenant-page.component.scss",
    imports: [
        CommonModule,
        RouterLink,
        UserDropdownComponent,
        MatIconModule,
        LoadingUntilFirstEmitDirective,
    ],
})
export class SelectTenantPageComponent {
    private authenticationService = inject(AuthenticationService);
    private router = inject(Router);

    public user$: Observable<User | undefined>;
    public isGlobalAdmin$: Observable<boolean>;
    public accessibleTenants$: Observable<TenantWrapper[]>;
    public allTenants$: Observable<TenantWrapper[]>;
    public isMobile$: Observable<boolean>;

    public constructor() {
        const authenticationService = this.authenticationService;
        const tenantService = inject(TenantService);
        const responsiveService = inject(ResponsiveService);

        this.user$ = authenticationService.user$;
        this.isGlobalAdmin$ = authenticationService.user$.pipe(map((u) => !!u?.isGlobalAdmin));
        this.accessibleTenants$ = authenticationService.user$.pipe(
            map((u) => u?.getAccessibleTenants() ?? []),
            this.sortTenants(),
            this.generateWrapper(),
        );
        this.allTenants$ = tenantService
            .getAllTenants()
            .pipe(this.sortTenants(), this.generateWrapper());
        this.isMobile$ = responsiveService.isMobile$;
    }

    public async logout() {
        await this.authenticationService.logout();
        await this.router.navigateByUrl("/login");
    }

    private sortTenants() {
        return map<TenantDto[], TenantDto[]>((tenants) => {
            return tenants.sort((a, b) => a.name.localeCompare(b.name));
        });
    }

    private generateWrapper() {
        return map<TenantDto[], TenantWrapper[]>((tenants) => {
            return tenants.map((t) => ({
                tenant: t,
                image: t.logo ?? "/assets/group_placeholder.svg",
            }));
        });
    }
}
